import React, { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import { X } from './Icons';
import useHaptics from '../hooks/useHaptics';
import { apiGet, apiPut, apiDelete, apiPost, apiPatch } from '../lib/api';
import ConfirmActionModal from './ConfirmActionModal';
import './ActivityDetail.css';

/* ── helpers ─────────────────────────────────────────── */

const CATEGORIES = [
  'Work', 'School', 'Health', 'Deen', 'Business',
  'Personal', 'Admin', 'Focus', 'Growth', 'Life', 'Wellbeing',
];

const FREQ_OPTIONS = [
  { value: 'daily',          label: 'Daily' },
  { value: 'weekdays',       label: 'Weekdays' },
  { value: 'specific_days',  label: 'Custom' },
];

const SNAP = 5;
const FALLBACK_MAX = 240;

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const snap  = (n) => Math.round(n / SNAP) * SNAP;

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return isoDate(d);
}

function todayKey() { return isoDate(new Date()); }

/* "Mon, May 5" — date pretty format */
function fmtLogDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map((n) => Number(n));
  if (!y || !m || !d) return iso;
  const dt = new Date(y, m - 1, d);
  const wk  = dt.toLocaleDateString(undefined, { weekday: 'short' });
  const mon = dt.toLocaleDateString(undefined, { month: 'short' });
  return `${wk}, ${mon} ${d}`;
}

function fmtFreqShort(activity) {
  const freq = activity?.schedule_rules?.frequency_type;
  const map = {
    daily: 'daily',
    weekdays: 'weekdays',
    weekly_total: 'weekly',
    specific_days: 'custom',
  };
  return map[freq] || null;
}

function priorityLabel(val) {
  const n = Number(val);
  if (n <= 4) return 'Low';
  if (n <= 6) return 'Normal';
  return 'High';
}

function priorityToNum(label) {
  if (label === 'Low') return 3;
  if (label === 'High') return 8;
  return 5;
}

const showToast = (msg, kind = 'info') => {
  if (typeof window !== 'undefined' && typeof window.showHxToast === 'function') {
    window.showHxToast(msg, kind);
  }
};

/* ── component ───────────────────────────────────────── */

export default function ActivityDetail({
  open,
  userId,
  activityId,
  initialActivity,
  onClose,
  onSave,
  onDelete,
}) {
  const { trigger } = useHaptics();

  const [activity, setActivity] = useState(initialActivity || null);
  const [loading, setLoading] = useState(false);
  const [error, setError]  = useState(null);

  // editable fields (local copy)
  const [name, setName]         = useState('');
  const [time, setTime]         = useState('');
  const [anytime, setAnytime]   = useState(false);
  const [duration, setDuration] = useState('');
  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState('Normal');
  const [frequency, setFrequency] = useState('daily');
  const [notes, setNotes]       = useState('');

  // derived stats
  const [streak, setStreak]         = useState(null);
  const [weekMins, setWeekMins]     = useState(null);
  const [completion, setCompletion] = useState(null);
  const [recentLogs, setRecentLogs] = useState(null);

  // today's slider state (live)
  const [todayMins, setTodayMins]   = useState(0);
  const [savingLog, setSavingLog]   = useState(false);
  const [dragging, setDragging]     = useState(false);

  // settings collapse
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [saving, setSaving]   = useState(false);
  const [deleting, setDelete] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [logToDelete, setLogToDelete] = useState(null);

  const overlayRef = useRef(null);
  const nameInputRef = useRef(null);
  const trackRef = useRef(null);
  const initialAtDragRef = useRef(0);
  const pointerIdRef = useRef(null);
  const confirmTimerRef = useRef(null);
  const streaksReqIdRef = useRef(0);
  const logsReqIdRef = useRef(0);

  /* populate form when activity data arrives */
  const hydrate = useCallback((a) => {
    if (!a) return;
    setName(a.name || '');
    setTime(a.preferred_time || '');
    setAnytime(!a.preferred_time);
    setDuration(String(a.duration_minutes || ''));
    setCategory(a.category || '');
    const pNum = a.priority != null ? Number(a.priority) : 5;
    setPriority(pNum <= 4 ? 'Low' : pNum <= 6 ? 'Normal' : 'High');
    setFrequency(a?.schedule_rules?.frequency_type || 'daily');
    setNotes(a.notes || '');
  }, []);

  /* reset on open */
  useEffect(() => {
    if (open) {
      setIsDirty(false);
      setConfirmingDelete(false);
      setSettingsOpen(false);
    }
  }, [open]);

  /* cleanup confirm timer on unmount */
  useEffect(() => () => {
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
  }, []);

  /* fetch activity detail */
  useEffect(() => {
    if (!open) return;
    setError(null);

    if (initialActivity) {
      if (!isDirty) {
        setActivity(initialActivity);
        hydrate(initialActivity);
      }
      return;
    }
    if (!activityId || !userId) return;

    let cancelled = false;
    setLoading(true);
    apiGet(`/api/activities/detail/${userId}/${activityId}`)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data) => {
        if (cancelled) return;
        const a = data?.activity ?? data;
        setActivity(a);
        if (!isDirty) hydrate(a);
      })
      .catch(() => { if (!cancelled) setError('Could not load activity.'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activityId, userId, initialActivity, hydrate]);

  /* parse a streak payload + extract value for this activity (shared helper) */
  const extractStreak = useCallback((data) => {
    const streaksRoot = data?.streaks ?? data;
    if (Array.isArray(streaksRoot)) {
      const entry = streaksRoot.find(
        (e) => e.activity_id === activityId || e.id === activityId
      );
      return entry?.current_streak ?? entry?.streak ?? null;
    }
    if (typeof streaksRoot === 'number') return streaksRoot;
    if (streaksRoot && typeof streaksRoot === 'object') {
      const val = streaksRoot[activityId];
      return val ?? null;
    }
    return null;
  }, [activityId]);

  /* refetch just the streak (used after deleting a log) */
  const refetchStreak = useCallback(async () => {
    if (!userId || !activityId) return;
    const myReqId = ++streaksReqIdRef.current;
    try {
      const r = await apiGet(`/api/streaks/${userId}`);
      if (!r.ok) return;
      const data = await r.json().catch(() => null);
      if (data == null) return;
      if (myReqId !== streaksReqIdRef.current) return; // stale
      setStreak(extractStreak(data));
    } catch (_) { /* leave stale value if endpoint shape differs */ }
  }, [userId, activityId, extractStreak]);

  /* fetch stats when activity id is known */
  useEffect(() => {
    if (!open || !activityId || !userId) return;
    let cancelled = false;

    const cutoff = daysAgo(7);
    const today  = todayKey();
    const streaksReqId = ++streaksReqIdRef.current;
    const logsReqId = ++logsReqIdRef.current;

    // streaks
    apiGet(`/api/streaks/${userId}`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        if (streaksReqId !== streaksReqIdRef.current) return; // stale
        setStreak(extractStreak(data));
      })
      .catch(() => { /* leave stale */ });

    // logs for this activity (last 7 days)
    apiGet(`/api/logs/${userId}`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        if (logsReqId !== logsReqIdRef.current) return; // stale
        const logs = Array.isArray(data) ? data : (data?.logs ?? []);
        const relevant = logs.filter(
          (l) =>
            (l.activity_id === activityId || l.id === activityId) &&
            l.date >= cutoff
        );
        const mins = relevant.reduce((sum, l) => sum + (l.actual_minutes || 0), 0);
        setWeekMins(mins);
        const days = new Set(relevant.map((l) => l.date));
        setCompletion(Math.round((days.size / 7) * 100));
        const sorted = [...relevant].sort((a, b) => (b.date > a.date ? 1 : -1)).slice(0, 7);
        setRecentLogs(sorted);
        // Today's total minutes (used by big slider)
        const todayTotal = relevant
          .filter((l) => l.date === today)
          .reduce((sum, l) => sum + (l.actual_minutes || 0), 0);
        setTodayMins(snap(todayTotal));
      })
      .catch(() => { /* leave stale */ });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activityId, userId]);

  /* ESC to close */
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  /* Cross-component log-change broadcast */
  useEffect(() => {
    if (!open || !activityId || !userId) return;
    const onLogsChanged = () => {
      const cutoff = daysAgo(7);
      const today  = todayKey();
      const myReqId = ++logsReqIdRef.current;
      apiGet(`/api/logs/${userId}`)
        .then((r) => r.json())
        .then((data) => {
          if (myReqId !== logsReqIdRef.current) return;
          const logs = Array.isArray(data) ? data : (data?.logs ?? []);
          const relevant = logs.filter(
            (l) =>
              (l.activity_id === activityId || l.id === activityId) &&
              l.date >= cutoff
          );
          const mins = relevant.reduce((sum, l) => sum + (l.actual_minutes || 0), 0);
          setWeekMins(mins);
          const days = new Set(relevant.map((l) => l.date));
          setCompletion(Math.round((days.size / 7) * 100));
          const sorted = [...relevant].sort((a, b) => (b.date > a.date ? 1 : -1)).slice(0, 7);
          setRecentLogs(sorted);
          if (!dragging && !savingLog) {
            const todayTotal = relevant
              .filter((l) => l.date === today)
              .reduce((sum, l) => sum + (l.actual_minutes || 0), 0);
            setTodayMins(snap(todayTotal));
          }
        })
        .catch(() => { /* leave stale */ });
      refetchStreak();
    };
    window.addEventListener('hx:logs-changed', onLogsChanged);
    return () => window.removeEventListener('hx:logs-changed', onLogsChanged);
  }, [open, activityId, userId, refetchStreak, dragging, savingLog]);

  /* reset stats on close */
  useEffect(() => {
    if (!open) {
      setStreak(null); setWeekMins(null);
      setCompletion(null); setRecentLogs(null);
      setTodayMins(0); setSavingLog(false); setDragging(false);
      setError(null); setIsDirty(false);
      setConfirmingDelete(false);
      setLogToDelete(null);
      setSettingsOpen(false);
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
    }
  }, [open]);

  /* recompute derived stats from logs (used after deleting a log) */
  const recomputeStats = useCallback((logs) => {
    const today = todayKey();
    const mins = logs.reduce((sum, l) => sum + (l.actual_minutes || 0), 0);
    setWeekMins(mins);
    const days = new Set(logs.map((l) => l.date));
    setCompletion(Math.round((days.size / 7) * 100));
    const todayTotal = logs
      .filter((l) => l.date === today)
      .reduce((sum, l) => sum + (l.actual_minutes || 0), 0);
    setTodayMins(snap(todayTotal));
  }, []);

  /* slider math */
  const planned = Number(activity?.duration_minutes) || Number(duration) || 0;
  const sliderMax = planned > 0 ? Math.max(planned, SNAP) : FALLBACK_MAX;
  const sliderPct = sliderMax > 0
    ? Math.min(100, (todayMins / sliderMax) * 100)
    : 0;
  const goalMet = planned > 0 && todayMins >= planned;

  const valueFromClientX = useCallback((clientX) => {
    const el = trackRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return 0;
    const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
    return clamp(snap(ratio * sliderMax), 0, sliderMax);
  }, [sliderMax]);

  /* Persist the today's-log value (PATCH most-recent-today / POST new) */
  const persistTodayMins = useCallback(async (next, prev) => {
    if (!userId || !activityId) {
      setTodayMins(prev);
      showToast("Couldn't save — try again.", 'error');
      return;
    }
    setSavingLog(true);
    try {
      const date = todayKey();
      let mostRecentId = null;
      try {
        const r = await apiGet(`/api/logs/${userId}?date=${date}`);
        if (r.ok) {
          const list = await r.json().catch(() => []);
          const arr = Array.isArray(list) ? list : (list?.logs ?? []);
          const mine = arr.filter((l) => l && l.activity_id === activityId);
          mine.sort((a, b) => {
            const ka = a.started_at || a.created_at || '';
            const kb = b.started_at || b.created_at || '';
            return kb > ka ? 1 : -1;
          });
          mostRecentId = mine[0]?.log_id || mine[0]?.id || null;
        }
      } catch (_) { /* fall through to POST */ }

      let res;
      if (mostRecentId) {
        res = await apiPatch(`/api/logs/${userId}/${mostRecentId}`, {
          actual_minutes: next,
        });
      } else {
        res = await apiPost(`/api/logs/${userId}`, {
          activity_id: activityId,
          actual_minutes: next,
          date,
        });
      }
      if (!res.ok) {
        setTodayMins(prev);
        showToast("Couldn't save — try again.", 'error');
        return;
      }
      trigger('light');
      try {
        window.dispatchEvent(
          new CustomEvent('hx:logs-changed', {
            detail: { activityId, minutes: next },
          })
        );
      } catch (_) { /* non-DOM env */ }
    } catch {
      setTodayMins(prev);
      showToast("Couldn't save — try again.", 'error');
    } finally {
      setSavingLog(false);
    }
  }, [activityId, userId, trigger]);

  /* slider pointer handlers */
  const onPointerDown = (e) => {
    if (savingLog) return;
    initialAtDragRef.current = todayMins;
    pointerIdRef.current = e.pointerId;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    setDragging(true);
    setTodayMins(valueFromClientX(e.clientX));
    e.preventDefault();
  };

  const onPointerMove = (e) => {
    if (!dragging || pointerIdRef.current !== e.pointerId) return;
    setTodayMins(valueFromClientX(e.clientX));
  };

  const onPointerUp = (e) => {
    if (!dragging) return;
    if (pointerIdRef.current != null && pointerIdRef.current !== e.pointerId) return;
    setDragging(false);
    pointerIdRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
    const next = valueFromClientX(e.clientX);
    setTodayMins(next);
    if (next !== initialAtDragRef.current) {
      persistTodayMins(next, initialAtDragRef.current);
    }
  };

  const onPointerCancel = () => {
    if (!dragging) return;
    setDragging(false);
    pointerIdRef.current = null;
    setTodayMins(initialAtDragRef.current);
  };

  const onSliderKeyDown = (e) => {
    if (savingLog) return;
    let delta = 0;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') delta = SNAP;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') delta = -SNAP;
    else if (e.key === 'Home') { e.preventDefault(); const prev = todayMins; setTodayMins(0); persistTodayMins(0, prev); return; }
    else if (e.key === 'End')  { e.preventDefault(); const prev = todayMins; setTodayMins(sliderMax); persistTodayMins(sliderMax, prev); return; }
    else return;
    e.preventDefault();
    const prev = todayMins;
    const next = clamp(prev + delta, 0, sliderMax);
    if (next !== prev) {
      setTodayMins(next);
      persistTodayMins(next, prev);
    }
  };

  /* quick-add chips */
  const handleQuickAdd = (delta) => {
    if (savingLog) return;
    trigger('light');
    const prev = todayMins;
    const next = clamp(snap(prev + delta), 0, sliderMax);
    if (next === prev) return;
    setTodayMins(next);
    persistTodayMins(next, prev);
  };

  /* reset today */
  const handleResetToday = () => {
    if (savingLog || todayMins === 0) return;
    trigger('medium');
    const prev = todayMins;
    setTodayMins(0);
    persistTodayMins(0, prev);
  };

  /* derived display values (must be computed before any early return) */
  const headerPlannedPill = useMemo(() => {
    const parts = [];
    if (activity?.duration_minutes) parts.push(`${activity.duration_minutes} min`);
    const f = fmtFreqShort(activity);
    if (f) parts.push(f);
    if (activity?.preferred_time) parts.push(activity.preferred_time);
    return parts.length ? parts.join(' · ') : null;
  }, [activity]);

  if (!open) return null;

  /* ── handlers ─────────────────────────────────────── */

  const handleSave = async (e) => {
    e.preventDefault();
    if (!isDirty) return;
    if (!name.trim()) { setError('Name cannot be empty.'); return; }
    if (name.trim().length > 100) { setError('Name too long (max 100 chars).'); return; }
    setSaving(true);
    setError(null);
    trigger('light');
    try {
      const payload = {
        name: name.trim(),
        preferred_time: anytime ? null : (time || null),
        duration_minutes: duration ? Number(duration) : null,
        category: category || null,
        priority: priorityToNum(priority),
        notes: notes.trim() || null,
        schedule_rules: {
          ...(activity?.schedule_rules || {}),
          frequency_type: frequency,
        },
      };
      const res = await apiPut(`/api/activities/${userId}/${activityId}`, payload);
      if (!res.ok) {
        let errText = '';
        try { errText = await res.text(); } catch (_) { /* ignore */ }
        throw new Error(errText || `HTTP ${res.status}`);
      }
      const updated = await res.json().catch(() => ({}));
      trigger('success');
      const final = updated?.activity ?? updated ?? {};
      final.activity_id = final.activity_id || final.id || activityId;
      final.id = final.id || final.activity_id;
      onSave(final);
      onClose();
    } catch (err) {
      setError('Save failed. Please try again.');
      trigger('error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDelete(true);
    trigger('medium');
    try {
      const res = await apiDelete(`/api/activities/${userId}/${activityId}`);
      if (!res.ok) throw new Error();
      trigger('success');
      onDelete?.(activityId);
      onClose();
    } catch (err) {
      setError('Delete failed. Please try again.');
      trigger('error');
    } finally {
      setDelete(false);
      setConfirmingDelete(false);
    }
  };

  const handleDeleteClick = () => {
    trigger('light');
    setConfirmingDelete(true);
  };

  const confirmDeleteActivity = async () => { await handleDelete(); };

  const handleCancel = () => { trigger('light'); onClose(); };

  const requestLogDelete = (log) => {
    trigger('light');
    setLogToDelete(log);
  };

  const confirmLogDelete = async () => {
    const log = logToDelete;
    if (!log) return;
    const id = log.log_id || log.id;
    const res = await apiDelete(`/api/logs/${userId}/${id}`);
    if (!res.ok) {
      trigger('error');
      throw new Error('Could not delete log');
    }
    const next = (recentLogs || []).filter(
      (l) => (l.log_id || l.id) !== id
    );
    setRecentLogs(next);
    recomputeStats(next);
    refetchStreak();
    trigger('success');
    showToast('Log deleted.', 'info');
    setLogToDelete(null);
    try {
      window.dispatchEvent(
        new CustomEvent('hx:logs-changed', { detail: { activity_id: activityId } })
      );
    } catch (_) { /* ignore */ }
  };

  /* ── derived display values ───────────────────────── */

  const sliderLabel = planned > 0
    ? `${todayMins} / ${planned} min`
    : `${todayMins} min`;

  /* ── JSX ──────────────────────────────────────────── */

  return (
    <div
      className="hx-sheet-overlay"
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) handleCancel(); }}
      role="dialog"
      aria-modal="true"
      aria-label={activity?.name || 'Activity detail'}
    >
      <form className="hx-sheet ad-sheet" onSubmit={handleSave} onClick={(e) => e.stopPropagation()}>

        {/* ── header ──────────────────────────────────── */}
        <header className="hx-sheet-head ad-head">
          <div className="ad-head-left">
            <h2 className="ad-title">
              {loading ? <span className="ad-spinner" /> : (activity?.name || 'Activity')}
            </h2>
            {headerPlannedPill && (
              <span className="ad-planned-pill">{headerPlannedPill}</span>
            )}
          </div>
          <button type="button" className="icon-btn" onClick={handleCancel} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        {/* ── error banner ─────────────────────────────── */}
        {error && <div className="ad-error-banner" role="alert">{error}</div>}

        {/* ── body (scrollable) ─────────────────────────── */}
        <div className="hx-sheet-body ad-body">

          {/* ── PRIMARY: Today's progress ───────────────── */}
          <section className="ad-progress">
            <div className="ad-progress-head">
              <span className="eyebrow ad-eyebrow">Today</span>
              <span className={`ad-progress-value${goalMet ? ' is-met' : ''}`}>
                {sliderLabel}
              </span>
            </div>

            <div
              className={`ad-slider${dragging ? ' is-dragging' : ''}${goalMet ? ' is-met' : ''}${savingLog ? ' is-saving' : ''}`}
            >
              <div
                ref={trackRef}
                className="ad-slider-track"
                role="slider"
                tabIndex={0}
                aria-label={`Logged time for ${activity?.name || 'activity'}`}
                aria-valuemin={0}
                aria-valuemax={sliderMax}
                aria-valuenow={todayMins}
                aria-valuetext={sliderLabel}
                aria-busy={savingLog}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerCancel}
                onKeyDown={onSliderKeyDown}
              >
                <div className="ad-slider-fill" style={{ width: `${sliderPct}%` }} />
                <div className="ad-slider-handle" style={{ left: `${sliderPct}%` }} aria-hidden />
              </div>
            </div>

            <div className="ad-quickadd" role="group" aria-label="Quick add minutes">
              {[5, 10, 15, 30].map((n) => (
                <button
                  key={n}
                  type="button"
                  className="ad-chip"
                  onClick={() => handleQuickAdd(n)}
                  disabled={savingLog}
                >
                  +{n}
                </button>
              ))}
              <button
                type="button"
                className="ad-reset"
                onClick={handleResetToday}
                disabled={savingLog || todayMins === 0}
              >
                Reset
              </button>
            </div>
          </section>

          {/* ── stats row ──────────────────────────────── */}
          <section className="ad-stats">
            <div className="ad-stat">
              <span className="ad-stat-num">{streak != null ? streak : '—'}</span>
              <span className="ad-stat-label">Streak</span>
            </div>
            <div className="ad-stat">
              <span className="ad-stat-num">{weekMins != null ? weekMins : '—'}</span>
              <span className="ad-stat-label">Week min</span>
            </div>
            <div className="ad-stat">
              <span className="ad-stat-num ad-stat-pct">
                {completion != null ? `${completion}%` : '—'}
              </span>
              <span className="ad-stat-label">Done 7d</span>
            </div>
          </section>

          {/* ── recent logs ────────────────────────────── */}
          <section className="ad-logs">
            <p className="eyebrow ad-eyebrow ad-logs-title">Recent</p>
            {recentLogs === null && (
              <p className="ad-muted">Loading…</p>
            )}
            {recentLogs !== null && recentLogs.length === 0 && (
              <p className="ad-muted">No activity yet this week.</p>
            )}
            {recentLogs !== null && recentLogs.length > 0 && (
              <ul className="ad-logs-list">
                {recentLogs.map((log, i) => {
                  const logKey = log.log_id || log.id || i;
                  return (
                    <li key={logKey} className="ad-log-row">
                      <span className="ad-log-date">{fmtLogDate(log.date)}</span>
                      <span className="ad-log-mins">{log.actual_minutes ?? '—'} min</span>
                      <button
                        type="button"
                        className="icon-btn ad-log-del"
                        onClick={() => requestLogDelete(log)}
                        aria-label={`Delete ${log.actual_minutes ?? ''}-minute log from ${log.date}`}
                      >
                        <X size={14} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* ── settings (collapsed by default) ────────── */}
          <section className="ad-settings">
            <button
              type="button"
              className="ad-settings-toggle"
              onClick={() => setSettingsOpen((v) => !v)}
              aria-expanded={settingsOpen}
            >
              <span>Settings</span>
              <span className={`ad-settings-chev${settingsOpen ? ' is-open' : ''}`} aria-hidden>›</span>
            </button>

            {settingsOpen && (
              <div className="ad-settings-body">
                <div className="hx-field">
                  <label className="eyebrow ad-eyebrow" htmlFor="ad-name">Name</label>
                  <input
                    id="ad-name"
                    ref={nameInputRef}
                    type="text"
                    value={name}
                    onChange={(e) => { setName(e.target.value); setIsDirty(true); }}
                    placeholder="Activity name"
                    maxLength={100}
                    required
                  />
                </div>

                <div className="hx-field">
                  <label className="eyebrow ad-eyebrow" htmlFor="ad-duration">Planned duration</label>
                  <div className="ad-suffix-input">
                    <input
                      id="ad-duration"
                      type="number"
                      min="1"
                      max="1440"
                      value={duration}
                      onChange={(e) => { setDuration(e.target.value); setIsDirty(true); }}
                      placeholder="30"
                    />
                    <span className="ad-suffix">min</span>
                  </div>
                </div>

                <div className="hx-field">
                  <label className="eyebrow ad-eyebrow">Frequency</label>
                  <div className="ad-pills" role="group" aria-label="Frequency">
                    {FREQ_OPTIONS.map((f) => (
                      <button
                        key={f.value}
                        type="button"
                        className={`ad-pill${frequency === f.value ? ' is-active' : ''}`}
                        onClick={() => { setFrequency(f.value); setIsDirty(true); }}
                        aria-pressed={frequency === f.value}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="hx-field">
                  <label className="eyebrow ad-eyebrow" htmlFor="ad-time">Preferred time</label>
                  <div className="ad-time-row">
                    <input
                      id="ad-time"
                      type="time"
                      value={time}
                      disabled={anytime}
                      onChange={(e) => { setTime(e.target.value); setIsDirty(true); }}
                    />
                    <label className="ad-anytime">
                      <input
                        type="checkbox"
                        checked={anytime}
                        onChange={(e) => {
                          setAnytime(e.target.checked);
                          if (e.target.checked) setTime('');
                          setIsDirty(true);
                        }}
                      />
                      <span>Anytime</span>
                    </label>
                  </div>
                </div>

                <div className="hx-field">
                  <label className="eyebrow ad-eyebrow" htmlFor="ad-category">Category</label>
                  <select
                    id="ad-category"
                    value={category}
                    onChange={(e) => { setCategory(e.target.value); setIsDirty(true); }}
                    className="ad-select"
                  >
                    <option value="">— select —</option>
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div className="hx-field">
                  <label className="eyebrow ad-eyebrow">Priority</label>
                  <div className="ad-pills" role="group" aria-label="Priority">
                    {['Low', 'Normal', 'High'].map((p) => (
                      <button
                        key={p}
                        type="button"
                        className={`ad-pill${priority === p ? ' is-active' : ''}`}
                        onClick={() => { setPriority(p); setIsDirty(true); }}
                        aria-pressed={priority === p}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="hx-field">
                  <label className="eyebrow ad-eyebrow" htmlFor="ad-notes">Notes</label>
                  <textarea
                    id="ad-notes"
                    rows={2}
                    value={notes}
                    onChange={(e) => { setNotes(e.target.value); setIsDirty(true); }}
                    placeholder="Optional notes…"
                  />
                </div>
              </div>
            )}
          </section>

          {/* ── danger zone ───────────────────────────── */}
          {onDelete && (
            <div className="ad-danger">
              <button
                type="button"
                className="ad-delete-link"
                onClick={handleDeleteClick}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete activity'}
              </button>
            </div>
          )}
        </div>

        {/* ── footer ──────────────────────────────────── */}
        <footer className="hx-sheet-foot ad-foot">
          <button type="button" className="btn-ghost" onClick={handleCancel}>
            Close
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={!isDirty || saving || loading}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </footer>

      </form>

      {/* Per-log delete confirmation */}
      <ConfirmActionModal
        open={!!logToDelete}
        title="Delete this log?"
        message={
          logToDelete
            ? `Remove the ${logToDelete.actual_minutes ?? 0}-minute log from ${fmtLogDate(logToDelete.date)}?`
            : ''
        }
        confirmLabel="Delete"
        destructive
        requirePassword={false}
        requireTypedText={false}
        onClose={() => setLogToDelete(null)}
        onConfirm={confirmLogDelete}
      />

      {/* Activity delete confirmation */}
      <ConfirmActionModal
        open={confirmingDelete}
        title={`Delete ${activity?.name || 'activity'}?`}
        message="This will permanently remove the activity. Existing logs will remain."
        confirmLabel="Delete"
        destructive
        requirePassword={false}
        requireTypedText={false}
        onClose={() => setConfirmingDelete(false)}
        onConfirm={confirmDeleteActivity}
      />
    </div>
  );
}
